/**
 * Meta 標籤管理 Composable
 * 動態更新頁面的 Title、Meta Description、Hreflang 和 Canonical 標籤
 */

import { computed, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { getMetaForLocale, localeToHreflang, PRODUCTION_DOMAIN } from '../config/meta'
import { getLocaleFromPath, removeLocaleFromPath, supportedLocales } from '../router/utils'

/**
 * 獲取當前生產環境域名（支持開發環境和生產環境）
 */
function getBaseDomain(): string {
  // 在開發環境可以從環境變數獲取，生產環境使用固定域名
  if (import.meta.env.DEV) {
    // 開發環境使用當前域名
    return `${window.location.protocol}//${window.location.host}`
  }
  return PRODUCTION_DOMAIN
}

/**
 * 動態更新 Meta 標籤
 */
export function useMeta() {
  const route = useRoute()

  // 從路由中提取語言代碼
  const currentLocale = computed(() => {
    return getLocaleFromPath(route.path) || 'en'
  })

  // 獲取當前語言的 Meta 配置
  const meta = computed(() => {
    return getMetaForLocale(currentLocale.value)
  })

  // 獲取當前路徑（不包含語言前綴）
  const currentPath = computed(() => {
    const path = removeLocaleFromPath(route.path)
    // 確保路徑以 / 開頭
    return path.startsWith('/') ? path : `/${path}`
  })

  // 獲取完整 URL（包含語言前綴）
  const currentUrl = computed(() => {
    const baseDomain = getBaseDomain()
    const locale = currentLocale.value
    const path = currentPath.value === '/' ? '' : currentPath.value
    return `${baseDomain}/${locale}${path}`
  })

  /**
   * 更新 Title 標籤
   */
  function updateTitle() {
    if (meta.value.title) {
      document.title = meta.value.title
      
      // 同時更新 meta[name="title"] 標籤
      let titleMeta = document.querySelector('meta[name="title"]') as HTMLMetaElement
      if (!titleMeta) {
        titleMeta = document.createElement('meta')
        titleMeta.name = 'title'
        document.head.appendChild(titleMeta)
      }
      titleMeta.content = meta.value.title
    }
  }

  /**
   * 更新 Meta Description
   */
  function updateDescription() {
    if (meta.value.description) {
      // 更新 meta[name="description"]
      let descMeta = document.querySelector('meta[name="description"]') as HTMLMetaElement
      if (!descMeta) {
        descMeta = document.createElement('meta')
        descMeta.name = 'description'
        document.head.appendChild(descMeta)
      }
      descMeta.content = meta.value.description

      // 同時更新 Open Graph description
      let ogDescMeta = document.querySelector('meta[property="og:description"]') as HTMLMetaElement
      if (!ogDescMeta) {
        ogDescMeta = document.createElement('meta')
        ogDescMeta.setAttribute('property', 'og:description')
        document.head.appendChild(ogDescMeta)
      }
      ogDescMeta.content = meta.value.description

      // 同時更新 Twitter description
      let twitterDescMeta = document.querySelector('meta[name="twitter:description"]') as HTMLMetaElement
      if (!twitterDescMeta) {
        twitterDescMeta = document.createElement('meta')
        twitterDescMeta.name = 'twitter:description'
        document.head.appendChild(twitterDescMeta)
      }
      twitterDescMeta.content = meta.value.description
    }
  }

  /**
   * 更新 Open Graph Title
   */
  function updateOgTitle() {
    if (meta.value.title) {
      let ogTitleMeta = document.querySelector('meta[property="og:title"]') as HTMLMetaElement
      if (!ogTitleMeta) {
        ogTitleMeta = document.createElement('meta')
        ogTitleMeta.setAttribute('property', 'og:title')
        document.head.appendChild(ogTitleMeta)
      }
      ogTitleMeta.content = meta.value.title

      // 同時更新 Twitter title
      let twitterTitleMeta = document.querySelector('meta[name="twitter:title"]') as HTMLMetaElement
      if (!twitterTitleMeta) {
        twitterTitleMeta = document.createElement('meta')
        twitterTitleMeta.name = 'twitter:title'
        document.head.appendChild(twitterTitleMeta)
      }
      twitterTitleMeta.content = meta.value.title
    }
  }

  /**
   * 更新 Open Graph URL
   */
  function updateOgUrl() {
    const url = currentUrl.value
    let ogUrlMeta = document.querySelector('meta[property="og:url"]') as HTMLMetaElement
    if (!ogUrlMeta) {
      ogUrlMeta = document.createElement('meta')
      ogUrlMeta.setAttribute('property', 'og:url')
      document.head.appendChild(ogUrlMeta)
    }
    ogUrlMeta.content = url
  }

  /**
   * 更新 Open Graph Locale
   */
  function updateOgLocale() {
    const locale = currentLocale.value
    // Open Graph locale 格式：en_US, zh_TW, ja_JP
    const ogLocaleMap: Record<string, string> = {
      en: 'en_US',
      zh: 'zh_TW', // 默認使用繁體中文
      ja: 'ja_JP'
    }
    const ogLocale = ogLocaleMap[locale] || 'en_US'
    
    let ogLocaleMeta = document.querySelector('meta[property="og:locale"]') as HTMLMetaElement
    if (!ogLocaleMeta) {
      ogLocaleMeta = document.createElement('meta')
      ogLocaleMeta.setAttribute('property', 'og:locale')
      document.head.appendChild(ogLocaleMeta)
    }
    ogLocaleMeta.content = ogLocale
  }

  /**
   * 更新 Hreflang 標籤
   */
  function updateHreflang() {
    const baseDomain = getBaseDomain()
    const path = currentPath.value

    // 移除現有的 hreflang 標籤
    const existingHreflang = document.querySelectorAll('link[rel="alternate"][hreflang]')
    existingHreflang.forEach((el) => el.remove())

    // 為每個支持的語言創建 hreflang 標籤
    supportedLocales.forEach((locale) => {
      const hreflang = localeToHreflang[locale] || locale
      const url = `${baseDomain}/${locale}${path}`
      
      const link = document.createElement('link')
      link.rel = 'alternate'
      link.hreflang = hreflang
      link.href = url
      document.head.appendChild(link)
    })

    // 添加 x-default（默認語言為英文）
    const defaultUrl = `${baseDomain}/en${path}`
    const defaultLink = document.createElement('link')
    defaultLink.rel = 'alternate'
    defaultLink.hreflang = 'x-default'
    defaultLink.href = defaultUrl
    document.head.appendChild(defaultLink)
  }

  /**
   * 更新 Canonical 標籤
   */
  function updateCanonical() {
    const url = currentUrl.value
    
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement
    if (!canonicalLink) {
      canonicalLink = document.createElement('link')
      canonicalLink.rel = 'canonical'
      document.head.appendChild(canonicalLink)
    }
    canonicalLink.href = url
  }

  /**
   * 更新 HTML lang 屬性
   */
  function updateHtmlLang() {
    const locale = currentLocale.value
    // 將 URL 語言代碼轉換為 HTML lang 屬性格式
    const htmlLang = localeToHreflang[locale] || locale
    document.documentElement.lang = htmlLang
  }

  /**
   * 更新所有 Meta 標籤
   */
  function updateAllMeta() {
    updateHtmlLang()
    updateTitle()
    updateDescription()
    updateOgTitle()
    updateOgUrl()
    updateOgLocale()
    updateHreflang()
    updateCanonical()
  }

  // 監聽路由變化，自動更新 Meta 標籤
  watch(
    () => [route.path, currentLocale.value],
    () => {
      updateAllMeta()
    },
    { immediate: true }
  )

  // 組件掛載時更新一次
  onMounted(() => {
    updateAllMeta()
  })

  return {
    currentLocale,
    meta,
    currentPath,
    currentUrl,
    updateAllMeta
  }
}

